<template>
  <base-material-card
    icon="mdi-cog"
    title="Company Settings"
    class="px-5 py-3"
    style="position: relative"
  >
    <v-form
      ref="settingsForm"
      @submit.prevent="saveSettings"
    >
      <v-container>
        <v-card-text>
          <v-expansion-panels
            v-model="openPanels"
            multiple
            accordion
            flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Controls how overtime is capped and whether it is discarded or entered manually.'">
                Overtime & Time Tracking
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12">
                    <div class="setting-title-row">
                      <h4 class="text-subtitle-1 mb-2" v-tooltip="'Choose how much overtime a member is allowed before warnings or caps apply.'">Overtime Limit Enforcement</h4>
                    </div>
                    <v-radio-group v-model="overtimeLimitMode">
                      <v-radio
                        label="No limit enforcement"
                        value="none"
                        v-tooltip="'Overtime is calculated normally with no enforced cap.'"
                      />
                      <v-radio
                        label="Daily limit (4 hrs/day, 12 hrs/week)"
                        value="daily"
                        v-tooltip="'Enforces a daily overtime limit. If a member exceeds 4 hours OT in a day or 12 hours in a week (90%), a warning is triggered.'"
                      />
                      <v-radio
                        label="Weekly limit (12 hrs/week)"
                        value="weekly"
                        v-tooltip="'Enforces a weekly overtime cap of 12 hours.'"
                      />
                      <v-radio
                        label="Monthly limit (48 hrs/month)"
                        value="monthly"
                        v-tooltip="'Enforces a monthly overtime cap of 48 hours.'"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="limitOT"
                      label="Limit Overtime"
                      v-tooltip="'Caps after-work overtime so only hours beyond 8 per day count as overtime.'"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="discardOt"
                      label="Discard Overtime"
                      v-tooltip="'When enabled, overtime is set to null and excluded from calculations and reports even when present.'"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="allowManualOT"
                      label="Allow Manual Overtime"
                      v-tooltip="'When enabled, overtime can be entered manually rather than being derived only from clock records.'"
                      hide-details
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="clockOutCorrectionGap"
                      label="Clock-Out Correction Gap (hours)"
                      v-tooltip="'Minimum hours between two clock-out punches before the earlier one is corrected to clock-in. Default 5.'"
                      type="number"
                      min="0"
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Controls how many non-Super users can be assigned the super-admin role.'">
                Users & Permissions
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="maxSuperAdmins"
                      :items="superAdminLimitOptions"
                      label="Max Super Admins"
                      placeholder="Unlimited"
                      v-tooltip="'Maximum number of non-Super users allowed to have the super-admin role. Leave unset to allow unlimited super admins.'"
                      clearable
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
             <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Controls how missed or late hours are sourced and calculated for each time record.'">
                Missed Hours
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="useInOutMissHour"
                      label="Use Clock-In/Clock-Out Missed Hours"
                      v-tooltip="'Uses clock-in/out deviations to calculate missed hours instead of the aggregate missed-hours field.'"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="usedMissedHours"
                      label="Alternative Missed Hours Field"
                      placeholder="e.g. lateHour"
                      v-tooltip="'Field name used for missed hours when not using clock-in/out values; leave empty to keep the usual calculation.'"
                      clearable
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>


            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Applies percentage or scale adjustments to pay hours and adjusts absent or penalty behavior.'">
                Pay & Attendance Adjustment
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="useScaleAdjustment"
                      label="Use Scale Adjustment"
                      v-tooltip="'When enabled, pay hours are adjusted with the scale formula instead of the percentage adjustment.'"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="discardAbsent"
                      label="Discard Absent Hours"
                      v-tooltip="'When enabled, absent hours are removed from full expected hours so absences do not affect attendance calculations.'"
                      hide-details
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="adjustmentPercent"
                      label="Adjustment Percent (%)"
                      v-tooltip="'Percentage applied to pay hours when scale adjustment is off; 0 means no change.'"
                      type="number"
                      min="0"
                      max="100"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="penaltyMinutes"
                      label="Penalty Minutes Interval"
                      v-tooltip="'Rounds missed or late time up to this interval for penalty calculations.'"
                      type="number"
                      min="1"
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Manages report recipients, auto-invites, and scheduled report generation.'">
                Notifications & Clock-In Status
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-textarea
                      v-model="CCEmailReportToText"
                      label="CC Email Recipients"
                      placeholder="One email per line"
                      v-tooltip="'One email address per line to include in report CC.'"
                      dense
                      outlined
                      rows="3"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-textarea
                      v-model="BCCEmailReportToText"
                      label="BCC Email Recipients"
                      placeholder="One email per line"
                      v-tooltip="'One email address per line to include in report BCC.'"
                      dense
                      outlined
                      rows="3"
                    />
                  </v-col>
                </v-row>
                 <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="sendInvite"
                        label="Auto-send Invite to New Recipients"
                        v-tooltip="'Automatically sends an invitation to email addresses added as report recipients.'"
                        hide-details
                        dense
                      />
                    </v-col>
                 </v-row>
                 <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="dailyReport"
                        label="Enable Daily Report"
                        v-tooltip="'Includes this company in the daily attendance report generation and delivery.'"
                        hide-details
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="dailyReportTime"
                        :items="reportTimeOptions"
                        item-text="text"
                        item-value="value"
                        label="Daily Report Time"
                        v-tooltip="'Select the time of day for generating the daily attendance report in 30-minute intervals.'"
                        dense
                        outlined
                      />
                    </v-col>
                 </v-row>
                 <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="weeklyReport"
                        label="Enable Weekly Report"
                        v-tooltip="'Includes this company in the weekly attendance report generation and delivery.'"
                        hide-details
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="weeklyReportDay"
                        :items="weekDayOptions"
                        item-text="text"
                        item-value="value"
                        label="Weekly Report Day"
                        v-tooltip="'Select the day of the week for generating the weekly attendance report.'"
                        dense
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="weeklyReportTime"
                        :items="reportHourOptions"
                        item-text="text"
                        item-value="value"
                        label="Weekly Report Time"
                        v-tooltip="'Select the time of day for generating the weekly attendance report by hour.'"
                        dense
                        outlined
                      />
                    </v-col>
                </v-row>
                <v-row>
                     <v-col cols="12" md="6">
                       <v-switch
                         v-model="overtimeWarning"
                         label="Enable Overtime Warning"
                         v-tooltip="'Sends overtime warning messages when members approach or exceed the configured overtime limit.'"
                         hide-details
                         dense
                       />
                     </v-col>
                </v-row>
                <v-row>
                     <v-col cols="12" md="6">
                       <v-switch
                         v-model="requestDigest"
                         label="Enable Request Digest"
                         v-tooltip="'Sends a once-daily batched summary of pending requests to team admins.'"
                         hide-details
                         dense
                       />
                     </v-col>
                     <v-col cols="12" md="6">
                       <v-select
                         v-model="requestDigestTime"
                         :items="reportTimeOptions"
                         item-text="text"
                         item-value="value"
                         label="Request Digest Time"
                         v-tooltip="'Select the time of day for sending the daily request digest summary.'"
                         dense
                         outlined
                       />
                     </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

<v-expansion-panel>
      <v-expansion-panel-header v-tooltip="'Company display assets used in reports, emails, and the app interface.'">
        Branding
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="logoUrl"
              label="Logo URL"
              placeholder="https://example.com/logo.png"
              v-tooltip="'Public image URL used as the company logo in reports and notifications.'"
              clearable
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="6" v-if="logoUrl">
            <img
              v-tooltip="'Preview of the company logo currently saved.'"
              :src="logoUrl"
              alt="Logo preview"
              style="max-height: 80px; max-width: 200px;"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-expansion-panel>
      <v-expansion-panel-header v-tooltip="'Rename Group and Parent Group labels (and their plurals) used across the app.'">
        Terminologies
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="groupLabel"
              label="Group Label"
              placeholder="Group"
              v-tooltip="'Custom label for Group (e.g. Department, Division). Leave empty to use default.'"
              clearable
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="parentGroupLabel"
              label="Parent Group Label"
              placeholder="Parent Group"
              v-tooltip="'Custom label for Parent Group (e.g. Function, Directorate). Leave empty to use default.'"
              clearable
              dense
              outlined
            />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-expansion-panel>
      <v-expansion-panel-header v-tooltip="'Controls the calendar type, date offset, and subscription expiry visible to Super users.'">
        Calendar & Date
      </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="calendar"
                      :items="calendarOptions"
                      label="Calendar Type"
                      v-tooltip="'Selects the calendar system used for date calculations across attendance and leave features.'"
                      dense
                      outlined
                    >
                      <template v-slot:item="{ item }">
                        <span v-tooltip="item.tooltip">{{ item.text }}</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="offsetDays"
                      label="Offset Days"
                      v-tooltip="'Number of days added or subtracted when calculating dates.'"
                      type="number"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col v-if="isSuperUser" cols="12" md="4">
                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="expiresFormatted"
                          label="Subscription Expiry"
                          prepend-icon="mdi-calendar"
                          v-tooltip="'Date when the company subscription expires; only visible to Super users.'"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          outlined
                        />
                      </template>
                      <v-date-picker
                        v-model="expires"
                        @input="dateMenu = false"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Controls annual leave accrual rules and entitlement adjustments based on service duration.'">
                Annual Leave
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="setting-title-row">
                      <h4 class="text-subtitle-1 mb-2" v-tooltip="'Choose when a member\'s leave year resets.'">Leave Cycle Mode</h4>
                    </div>
                    <v-radio-group v-model="leaveCycleMode">
                      <v-radio
                        label="Calendar Year"
                        value="calendar"
                        v-tooltip="'Leave year resets on January 1.'"
                      />
                      <v-radio
                        label="Anniversary Year"
                        value="anniversary"
                        v-tooltip="'Leave year resets on each member\'s joining-date anniversary.'"
                      />
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="leaveCycleStarts"
                      label="Cycle Joining Date (MM-DD)"
                      placeholder="e.g. 01-01"
                      v-tooltip="'Date used as the cycle start or reference point for leave calculations.'"
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="leaveBaseEntitlement"
                      label="Base Entitlement (days/year)"
                      v-tooltip="'Starting annual leave days allocated to a member.'"
                      type="number"
                      min="0"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="leaveIncrementInterval"
                      label="Increment Interval (years)"
                      v-tooltip="'Number of completed service years before leave increases.'"
                      type="number"
                      min="1"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="leaveIncrementAmount"
                      label="Increment Amount (days)"
                      v-tooltip="'Extra leave days added after each increment interval.'"
                      type="number"
                      min="0"
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <div class="setting-title-row">
                      <h4 class="text-subtitle-1 mb-2" v-tooltip="'How much of this year\'s leave a member can use before the year ends.'">This Year's Leave</h4>
                    </div>
                    <v-radio-group v-model="leaveCurrentYearMode">
                      <v-radio
                        label="Grow monthly — earn a little leave each month"
                        value="prorate"
                        v-tooltip="'Members earn 1/12 of the yearly amount each month, starting from the month they joined.'"
                      />
                      <v-radio
                        label="Full — get the rest of the year upfront"
                        value="full"
                        v-tooltip="'Members can use the rest of the year\'s amount upfront, counted from the month they joined.'"
                      />
                      <v-radio
                        label="Deny — no new leave until the year ends"
                        value="deny"
                        v-tooltip="'Members earn nothing this year until it ends; leave carried from past years can still be used.'"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Fields that must be filled when creating or editing a member; leave empty to make all optional except Joining Date.'">
                Required Member Fields
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12">
                    <p class="mb-2">Select fields that must be filled when creating or editing a member. Leave empty to make all fields optional (except Joining Date which is always required).</p>
                    <v-select
                      v-model="requiredMemberFields"
                      :items="memberFieldOptions"
                      item-text="label"
                      item-value="value"
                      label="Required Member Fields"
                      v-tooltip="'Selects which member profile fields must be completed before a record can be saved.'"
                      multiple
                      chips
                      clearable
                      deletable-chips
                      dense
                      outlined
                    >
                      <template v-slot:item="{ item }">
                        <span v-tooltip="item.tooltip">{{ item.label }}</span>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header v-tooltip="'Configurable dropdown values used by member forms.'">
                Lists
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12">
                    <p class="mb-2">Member categories used as dropdown values in member forms.</p>
                    <v-row v-for="(item, index) in memberCategories" :key="index" align="center" class="mb-2">
                      <v-col cols="12" sm="5" md="5">
                        <v-text-field
                          v-model="item.label"
                          label="Label"
                          placeholder="e.g. Staff"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="5" md="5">
                        <v-text-field
                          v-model="item.value"
                          label="Value"
                          placeholder="e.g. staff"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" class="text-right">
                        <v-btn icon color="error" @click="removeMemberCategory(index)" v-tooltip="'Remove category'">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn color="secondary" @click="addMemberCategory" class="mt-2 mr-2" v-tooltip="'Add another category option'">
                      <v-icon left>mdi-plus</v-icon>
                      Add Category
                    </v-btn>
                    <p class="mb-2 mt-4">Job levels used as dropdown values in member forms.</p>
                    <v-row v-for="(item, index) in jobLevelOptions" :key="'job-'+index" align="center" class="mb-2">
                      <v-col cols="12" sm="5" md="5">
                        <v-text-field
                          v-model="item.label"
                          label="Label"
                          placeholder="e.g. Senior"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="5" md="5">
                        <v-text-field
                          v-model="item.value"
                          label="Value"
                          placeholder="e.g. senior"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" class="text-right">
                        <v-btn icon color="error" @click="removeJobLevel(index)" v-tooltip="'Remove job level'">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn color="secondary" @click="addJobLevel" class="mt-2" v-tooltip="'Add another job level option'">
                      <v-icon left>mdi-plus</v-icon>
                      Add Job Level
                    </v-btn>
                    <p class="mb-2 mt-4">Leave types used as dropdown values in attendance leave forms. Leave empty to use the default list.</p>
                    <v-row v-for="(item, index) in leaveTypes" :key="'leave-'+index" align="center" class="mb-2">
                      <v-col cols="12" sm="10" md="10">
                        <v-text-field
                          v-model="leaveTypes[index]"
                          label="Leave Type"
                          placeholder="e.g. Annual Leave"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" class="text-right">
                        <v-btn icon color="error" @click="removeLeaveType(index)" v-tooltip="'Remove leave type'">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn color="secondary" @click="addLeaveType" class="mt-2 mr-2" v-tooltip="'Add another leave type option'">
                      <v-icon left>mdi-plus</v-icon>
                      Add Leave Type
                    </v-btn>
                    <p class="mb-2 mt-4">Submission remarks used as suggestions when submitting time records. Leave empty to use the default list.</p>
                    <v-row v-for="(item, index) in submissionRemarks" :key="'remark-'+index" align="center" class="mb-2">
                      <v-col cols="12" sm="10" md="10">
                        <v-text-field
                          v-model="submissionRemarks[index]"
                          label="Submission Remark"
                          placeholder="e.g. Asked for permission"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" class="text-right">
                        <v-btn icon color="error" @click="removeSubmissionRemark(index)" v-tooltip="'Remove submission remark'">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn color="secondary" @click="addSubmissionRemark" class="mt-2" v-tooltip="'Add another submission remark'">
                      <v-icon left>mdi-plus</v-icon>
                      Add Submission Remark
                    </v-btn>
                    <p class="mb-2 mt-4">Termination reasons used as dropdown values when requesting member termination. Leave empty to use the default list.</p>
                    <v-row v-for="(item, index) in terminationReasons" :key="'termination-'+index" align="center" class="mb-2">
                      <v-col cols="12" sm="10" md="10">
                        <v-text-field
                          v-model="terminationReasons[index]"
                          label="Termination Reason"
                          placeholder="e.g. Contract End"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" class="text-right">
                        <v-btn icon color="error" @click="removeTerminationReason(index)" v-tooltip="'Remove termination reason'">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn color="secondary" @click="addTerminationReason" class="mt-2" v-tooltip="'Add another termination reason'">
                      <v-icon left>mdi-plus</v-icon>
                      Add Termination Reason
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-tooltip="'Saves all visible company settings for the current company.'"
            color="primary"
            type="submit"
            :loading="saving"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-container>
    </v-form>
  </base-material-card>
</template>

<script>
import { M_UPDATE_COMPANY, Q_GET_COMPANIES } from '@/gql/company'
import { Q_GET_USERS } from '@/gql/user'

export default {
  name: 'CompanySettings',
  data: () => ({
    overtimeLimitMode: 'none',
    limitOT: false,
    discardOt: false,
    allowManualOT: false,
    clockOutCorrectionGap: 5,
    useInOutMissHour: false,
    usedMissedHours: '',
    useScaleAdjustment: false,
    adjustmentPercent: 0,
    penaltyMinutes: null,
    discardAbsent: false,
    sendInvite: true,
    dailyReport: false,
    dailyReportTime: '23:00',
    weeklyReport: false,
    weeklyReportDay: 1,
    weeklyReportTime: '08:00',
    overtimeWarning: false,
    requestDigest: false,
    requestDigestTime: '09:00',
    maxSuperAdmins: null,
    CCEmailReportTo: [],
    BCCEmailReportTo: [],
    calendar: '',
    offsetDays: 0,
    leaveCycleMode: 'calendar',
    logoUrl: '',
    leaveCycleStarts: '01-01',
    leaveBaseEntitlement: 16,
    leaveIncrementInterval: 2,
    leaveIncrementAmount: 1,
    leaveCurrentYearMode: 'prorate',
      requiredMemberFields: [],
      memberCategories: [],
      jobLevelOptions: [],
      leaveTypes: [],
      submissionRemarks: [],
      terminationReasons: [],
      groupLabel: '',
      parentGroupLabel: '',
      saving: false,
    openPanels: [0],
    dateMenu: false,
    expires: null,
    expiresFormatted: '',
  }),
  computed: {
    company () {
      return this.$store.getters.companyFull || {}
    },
    companySettings () {
      return this.company.settings || {}
    },
    isSuperUser () {
      return this.$store.state.user?.name === 'Super'
    },
    existingSuperAdminCount () {
      return (this.users || []).filter(user =>
        user.name !== 'Super' &&
        Array.isArray(user.roles) &&
        user.roles.includes('super-admin'),
      ).length
    },
    superAdminLimitOptions () {
      const baseOptions = Array.from({ length: 10 }, (_, index) => index + 1)
      const minimum = Math.max(1, this.existingSuperAdminCount)
      const options = baseOptions.filter(value => value >= minimum)
      if (this.existingSuperAdminCount > 10) {
        return [...options, this.existingSuperAdminCount]
      }
      return options
    },
    calendarOptions () {
      return [
        { text: 'Gregorian', value: '', tooltip: 'Uses the standard Gregorian calendar for date calculations.' },
        { text: 'Ethiopian (EC)', value: 'EC', tooltip: 'Uses Ethiopian calendar conversions for dates and reports.' },
      ]
    },
    memberFieldOptions () {
      return [
        { label: 'Phone Number', value: 'phone', tooltip: 'Requires the member\'s phone number before saving.' },
        { label: 'Email', value: 'email', tooltip: 'Requires the member\'s email address before saving.' },
        { label: 'WorkID', value: 'workId', tooltip: 'Requires the member\'s work ID before saving.' },
        { label: 'Job Title', value: 'jobTitle', tooltip: 'Requires the member\'s job title before saving.' },
        { label: 'Job Level', value: 'jobLevel', tooltip: 'Requires the member\'s job level before saving.' },
        { label: 'Team', value: 'currentTeam', tooltip: 'Requires the member\'s primary team before saving.' },
      ]
    },
    reportTimeOptions () {
      const options = []
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
          const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          options.push({ text: label, value: label })
        }
      }
      return options
    },
    weekDayOptions () {
      return [
        { text: 'Sunday', value: 0 },
        { text: 'Monday', value: 1 },
        { text: 'Tuesday', value: 2 },
        { text: 'Wednesday', value: 3 },
        { text: 'Thursday', value: 4 },
        { text: 'Friday', value: 5 },
        { text: 'Saturday', value: 6 },
      ]
    },
    reportHourOptions () {
      const options = []
      for (let h = 0; h < 24; h++) {
        const label = `${String(h).padStart(2, '0')}:00`
        options.push({ text: label, value: label })
      }
      return options
    },
    CCEmailReportToText: {
      get () {
        return Array.isArray(this.CCEmailReportTo) ? this.CCEmailReportTo.join('\n') : ''
      },
      set (val) {
        this.CCEmailReportTo = val.split('\n').filter(email => email.trim()).map(email => email.trim())
      },
    },
    BCCEmailReportToText: {
      get () {
        return Array.isArray(this.BCCEmailReportTo) ? this.BCCEmailReportTo.join('\n') : ''
      },
      set (val) {
        this.BCCEmailReportTo = val.split('\n').filter(email => email.trim()).map(email => email.trim())
      },
    },
  },
  created () {
    this.loadSettings()
  },
  watch: {
    existingSuperAdminCount (count) {
      if (count > 0 && (!this.maxSuperAdmins || this.maxSuperAdmins < count)) {
        this.maxSuperAdmins = count
      }
    },
  },
  methods: {
    normalizeMemberCategoryList () {
      let parsed = []
      try {
        parsed = this.memberCategoryText.trim() ? JSON.parse(this.memberCategoryText) : []
      } catch (e) {
        this.$toast.error('Member category list must be valid JSON')
        return null
      }
      if (!Array.isArray(parsed)) {
        this.$toast.error('Member category list must be an array')
        return null
      }
      return parsed
        .filter(item => item && item.value && item.label)
        .map(item => ({
          value: String(item.value),
          label: String(item.label),
        }))
    },
    loadSettings () {
      const cs = this.companySettings
      this.overtimeLimitMode = cs.overtimeLimitMode || 'none'
      this.limitOT = cs.limitOT || false
      this.discardOt = cs.discardOt || false
      this.allowManualOT = cs.allowManualOT || false
      this.clockOutCorrectionGap = cs.clockOutCorrectionGap ?? 5
      this.useInOutMissHour = cs.useInOutMissHour || false
      this.usedMissedHours = cs.usedMissedHours || ''
      this.useScaleAdjustment = cs.useScaleAdjustment || false
      this.adjustmentPercent = cs.adjustmentPercent || 0
      this.penaltyMinutes = cs.penaltyMinutes || null
      this.discardAbsent = cs.discardAbsent || false
       this.sendInvite = cs.sendInvite !== false
       this.dailyReport = cs.dailyReport || false
        this.dailyReportTime = cs.dailyReportTime || '23:00'
        this.weeklyReport = cs.weeklyReport || false
       this.weeklyReportDay = cs.weeklyReportDay ?? 1
       this.weeklyReportTime = cs.weeklyReportTime || '08:00'
      this.overtimeWarning = cs.overtimeWarning || false
      this.requestDigest = cs.requestDigest || false
      this.requestDigestTime = cs.requestDigestTime || '09:00'
      this.maxSuperAdmins = cs.maxSuperAdmins ?? null
      this.CCEmailReportTo = cs.CCEmailReportTo || []
      this.BCCEmailReportTo = cs.BCCEmailReportTo || []
      this.calendar = cs.calendar || ''
      this.offsetDays = cs.offsetDays || 0
      this.leaveCycleMode = cs.leaveCycleMode || 'calendar'
      this.logoUrl = this.company.logo || ''
      this.leaveCycleStarts = cs.leaveCycleStarts || '01-01'
      this.leaveBaseEntitlement = cs.leaveBaseEntitlement || 16
      this.leaveIncrementInterval = cs.leaveIncrementInterval || 2
      this.leaveIncrementAmount = cs.leaveIncrementAmount || 1
      this.leaveCurrentYearMode = cs.leaveCurrentYearMode || 'prorate'
      this.requiredMemberFields = cs.requiredMemberFields || []
      this.memberCategories = (cs.lists && cs.lists.memberCategory) ? [...cs.lists.memberCategory] : []
      this.jobLevelOptions = (cs.lists && cs.lists.jobLevel) ? [...cs.lists.jobLevel] : []
      this.leaveTypes = (cs.lists && cs.lists.leaveTypes) ? [...cs.lists.leaveTypes] : []
      this.submissionRemarks = (cs.lists && cs.lists.submissionRemarks) ? [...cs.lists.submissionRemarks] : []
      this.terminationReasons = (cs.lists && cs.lists.terminationReasons) ? [...cs.lists.terminationReasons] : []
      this.groupLabel = (cs.terminology && cs.terminology.groupLabel) || ''
      this.parentGroupLabel = (cs.terminology && cs.terminology.parentGroupLabel) || ''
    },
    addMemberCategory () {
      this.memberCategories.push({ value: '', label: '' })
    },
    removeMemberCategory (index) {
      this.memberCategories.splice(index, 1)
    },
    addJobLevel () {
      this.jobLevelOptions.push({ value: '', label: '' })
    },
    removeJobLevel (index) {
      this.jobLevelOptions.splice(index, 1)
    },
    addLeaveType () {
      this.leaveTypes.push('')
    },
    removeLeaveType (index) {
      this.leaveTypes.splice(index, 1)
    },
    addSubmissionRemark () {
      this.submissionRemarks.push('')
    },
    removeSubmissionRemark (index) {
      this.submissionRemarks.splice(index, 1)
    },
    addTerminationReason () {
      this.terminationReasons.push('')
    },
    removeTerminationReason (index) {
      this.terminationReasons.splice(index, 1)
    },
    saveSettings () {
      this.saving = true
      const memberCategoryList = this.memberCategories
        .filter(item => item && item.value && item.label)
        .map(item => ({
          value: String(item.value),
          label: String(item.label),
        }))
      const jobLevelList = this.jobLevelOptions
        .filter(item => item && item.value && item.label)
        .map(item => ({
          value: String(item.value),
          label: String(item.label),
        }))
      const leaveTypes = this.leaveTypes
        .filter(item => item && String(item).trim())
        .map(item => String(item).trim())
      const submissionRemarks = this.submissionRemarks
        .filter(item => item && String(item).trim())
        .map(item => String(item).trim())
      const terminationReasons = this.terminationReasons
        .filter(item => item && String(item).trim())
        .map(item => String(item).trim())
      if (this.existingSuperAdminCount > 0 && (!this.maxSuperAdmins || this.maxSuperAdmins < this.existingSuperAdminCount)) {
        this.maxSuperAdmins = this.existingSuperAdminCount
      }
      const updatedSettings = {
        overtimeLimitMode: this.overtimeLimitMode,
        limitOT: this.limitOT,
        discardOt: this.discardOt,
        allowManualOT: this.allowManualOT,
        clockOutCorrectionGap: this.clockOutCorrectionGap,
        useInOutMissHour: this.useInOutMissHour,
        usedMissedHours: this.usedMissedHours || undefined,
        useScaleAdjustment: this.useScaleAdjustment,
        adjustmentPercent: this.adjustmentPercent,
        penaltyMinutes: this.penaltyMinutes || undefined,
        discardAbsent: this.discardAbsent,
        CCEmailReportTo: this.CCEmailReportTo,
        BCCEmailReportTo: this.BCCEmailReportTo,
        calendar: this.calendar || undefined,
        offsetDays: this.offsetDays,
        expires: this.expires || undefined,
        leaveCycleMode: this.leaveCycleMode,
        leaveCycleStarts: this.leaveCycleStarts,
        leaveBaseEntitlement: this.leaveBaseEntitlement,
        leaveIncrementInterval: this.leaveIncrementInterval,
        leaveIncrementAmount: this.leaveIncrementAmount,
        leaveCurrentYearMode: this.leaveCurrentYearMode,
        sendInvite: this.sendInvite,
        dailyReport: this.dailyReport,
        dailyReportTime: this.dailyReportTime,
        weeklyReport: this.weeklyReport,
        weeklyReportDay: this.weeklyReportDay,
        weeklyReportTime: this.weeklyReportTime,
        overtimeWarning: this.overtimeWarning,
        requestDigest: this.requestDigest,
        requestDigestTime: this.requestDigestTime,
        maxSuperAdmins: this.maxSuperAdmins || undefined,
        requiredMemberFields: this.requiredMemberFields,
        terminology: {
          groupLabel: this.groupLabel || undefined,
          parentGroupLabel: this.parentGroupLabel || undefined,
        },
        lists: {
          ...(this.companySettings.lists || {}),
          memberCategory: memberCategoryList,
          jobLevel: jobLevelList,
          leaveTypes,
          submissionRemarks,
          terminationReasons,
        },
      }

      const logo = this.logoUrl || null

      this.$apollo.mutate({
        mutation: M_UPDATE_COMPANY,
        variables: {
              id: this.company.id,
              settings: updatedSettings,
              logo,
        },
        update: (store, { data: { updateCompany } }) => {
            const data = store.readQuery({
              query: Q_GET_COMPANIES,
              variables: {
                where: { tenantId: localStorage.getItem('company') },
              },
            })
            if (data && data.companies) {
              const idx = data.companies.findIndex(c => c.id === updateCompany.id)
              if (idx !== -1) {
                data.companies[idx].settings = updateCompany.settings
                data.companies[idx].logo = updateCompany.logo
              }
              store.writeQuery({
                query: Q_GET_COMPANIES,
                variables: {
                  where: { tenantId: localStorage.getItem('company') },
                },
                data,
              })
            }
          },
        })        .then(() => {
          const updated = { ...this.company, settings: updatedSettings, logo }
          this.$store.commit('SET_COMPANY_FULL', updated)
          localStorage.setItem('companyFull', JSON.stringify(updated))
        this.saving = false
        this.$toast.info('Settings saved successfully')
      }).catch((error) => {
        this.saving = false
        console.error(error)
        this.$toast.error('Failed to save settings')
      })
    },
    apollo: {
      users: {
        query: Q_GET_USERS,
      },
    },
  },
}
</script>

<style scoped>
.setting-title-row {
  display: flex;
  align-items: center;
}
</style>
